import SalviaLayout from "@/components/SalviaLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { usePageTitle } from "@/hooks/usePageTitle";
import { KPICard, KPICardRow, StatusBadge } from "@/components/dashboard";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, FileText, CheckCircle2, Clock, XCircle } from "lucide-react";

export default function RHDocumentos() {
  usePageTitle("Documentos RH");
  const { user } = useAuth();
  const { data, isLoading, error } = trpc.dashboards.rh.documents.useQuery(undefined, {
    enabled: !!user && user.role === "client_rh",
    refetchOnWindowFocus: false,
  });

  if (error) {
    return (
      <SalviaLayout title="Dashboard — Documentos">
        <Card className="p-8 text-center">
          <AlertTriangle className="mx-auto mb-4 text-destructive" size={48} />
          <p className="text-muted-foreground">{error.message}</p>
        </Card>
      </SalviaLayout>
    );
  }

  const loading = isLoading || !data;

  return (
    <SalviaLayout title="Dashboard — Documentos">
      <div className="space-y-6">
        <KPICardRow>
          <KPICard
            label="Total Documentos"
            value={data?.cards.total ?? 0}
            icon={FileText}
            iconColor="text-blue-600"
            iconBg="bg-blue-100"
            loading={loading}
          />
          <KPICard
            label="Prontos"
            value={data?.cards.ready ?? 0}
            icon={CheckCircle2}
            iconColor="text-emerald-600"
            iconBg="bg-emerald-100"
            loading={loading}
          />
          <KPICard
            label="Pendentes"
            value={data?.cards.pending ?? 0}
            icon={Clock}
            iconColor="text-amber-600"
            iconBg="bg-amber-100"
            loading={loading}
          />
          <KPICard
            label="Com Falha"
            value={data?.cards.failed ?? 0}
            icon={XCircle}
            iconColor="text-red-600"
            iconBg="bg-red-100"
            loading={loading}
          />
        </KPICardRow>

        <Card className="p-6">
          <h3 className="text-sm font-semibold mb-4">Lista de Documentos</h3>
          {loading ? (
            <div className="space-y-3">
              {[1,2,3,4].map(i => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : (data?.documents ?? []).length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Nenhum documento gerado</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Tipo</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Gerado em</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {data!.documents.map((doc: any) => (
                    <tr key={doc.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-3 py-2.5 font-medium">{doc.type}</td>
                      <td className="px-3 py-2.5">
                        <StatusBadge status={doc.status === "READY" ? "AVALIACAO_CONCLUIDA" : doc.status === "FAILED" ? "AVALIACAO_BLOQUEADA_SEM_RESPOSTAS" : "PENDENTE"} />
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground">
                        {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString("pt-BR") : "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        {doc.url ? (
                          <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-xs font-medium">
                            Baixar PDF
                          </a>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </SalviaLayout>
  );
}
